import React, { useState, useEffect } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import './gopreview.scss';
import { getSquareId, getVisibleSquares } from './calculations';
import { generateRandomBarInfo } from './bar-info';
import { Square, Bar } from './types';

let requestCount = 0;

function showSpinner() {
	const spinner = document.getElementById('spinner');
	if (spinner) spinner.style.display = 'block';
}

function hideSpinner() {
	const spinner = document.getElementById('spinner');
	if (spinner) spinner.style.display = 'none';
}

function requestStarted() {
	requestCount++;
	if (requestCount === 1) showSpinner();
}

function requestCompleted() {
	requestCount--;
	if (requestCount === 0) hideSpinner();
}

const testCenter = { lat: 59.3293, lng: 18.0686 };

const GoPreview: React.FC = () => {
	const [squareSizeMeter, setSquareSizeMeter] = useState<number>(1500);
	const [maxBarsPerSquare, setMaxBarsPerSquare] = useState<number>(2);

	let map: google.maps.Map | undefined;

	console.log(
		'process.env.REACT_APP_MAPS_API_KEY',
		process.env.REACT_APP_MAPS_API_KEY,
	);

	async function initMap(): Promise<void> {
		const loader = new Loader({
			apiKey: process.env.REACT_APP_MAPS_API_KEY || '',
			version: 'weekly',
		});

		const mapOptions: google.maps.MapOptions = {
			center: testCenter,
			zoom: 8,
			mapId: 'a7be995dc1bee7fc',
		};

		await loader
			.importLibrary('maps')
			.then(({ Map }) => {
				map = new Map(
					document.getElementById('gmap') as HTMLElement,
					mapOptions,
				);
			})
			.catch((error) => {
				console.error(error);
			});

		const { AdvancedMarkerElement } = (await google.maps.importLibrary(
			'marker',
		)) as google.maps.MarkerLibrary;

		const squareCache: Record<string, Square> = {};
		const squaresBeingFetched: Record<string, string> = {};
		const displayedSquares: string[] = [];

		function createMarkerElement(): HTMLElement {
			const barMarker = document.createElement('div');
			const markerIcon = document.createElement('div');
			const markerCircle = document.createElement('div');
			barMarker.appendChild(markerIcon);
			barMarker.appendChild(markerCircle);

			barMarker.className = 'bar-marker';
			markerIcon.className = 'bar-icon';
			markerCircle.className = 'bar-circle';

			return barMarker;
		}

		function displaySquare(squareId: string) {
			const square = squareCache[squareId];

			if (!square) {
				console.error('Square not found in cache: ', squareId);
				return;
			}

			console.log(
				'displaying square:',
				squareId,
				', bars:',
				square.bars.length,
			);
			for (const key in square.bars) {
				const bar = square.bars[key];

				// Generate random info and create a custom marker if info is not already present
				if (!bar.info) {
					bar.info = generateRandomBarInfo();
				}

				if (!bar.marker) {
					bar.marker = new google.maps.marker.AdvancedMarkerElement({
						map,
						position: bar.coordinates,
						content: createMarkerElement(),
					});
				}

				bar.marker.map = map;
				console.log('displaying bar', bar.placeName);
			}

			console.log('displayedSquares', displayedSquares);
		}

		function hideSquare(squareId: string) {
			if (!squareCache[squareId]) {
				console.error('Square not found', squareId);
				return;
			}

			console.log('hiding square:', squareId);

			const bars = squareCache[squareId].bars;
			for (const key in bars) {
				const bar = bars[key];
				if (bar.marker) {
					bar.marker.map = null;
				}
			}
		}

		async function fetchSquares(squareSize: number, squareIds: string[]) {
			// Create an instance of URLSearchParams
			const params = new URLSearchParams();

			// Append squareSize as a string
			params.append('squareSize', squareSize.toString());

			// Append each squareId separately
			squareIds.forEach((id) => params.append('squareIds', id));

			// Convert the params to a query string
			const queryString = params.toString();

			// Fetch the data from the backend
			const response = await fetch(
				`http://localhost:3001/api/places?${queryString}`,
			);

			if (!response.ok) {
				throw new Error('Network response was not ok');
			}

			const data = await response.json();

			console.log('data', data);

			return data.squares;
		}

		map!.addListener('drag', async () => {
			if (map!.getZoom()! < 12) {
				displayedSquares.forEach(hideSquare);
				return;
			}

			const newVisibleSquares = getVisibleSquares(
				map!.getBounds()!,
				squareSizeMeter,
				true,
			);

			console.log('visibleSquares', newVisibleSquares);

			const displayedSquaresCopy = [...displayedSquares];

			displayedSquaresCopy.forEach((squareId) => {
				// Remove the square from new list if it's already displayed
				if (newVisibleSquares.includes(squareId)) {
					const index = newVisibleSquares.indexOf(squareId);
					newVisibleSquares.splice(index, 1);
				}
				// Hide the square if it's not in the new list
				else {
					hideSquare(squareId);
					displayedSquares.splice(
						displayedSquares.indexOf(squareId),
						1,
					);
				}
			});

			const squaresToFetch: string[] = [];

			newVisibleSquares.forEach((squareId) => {
				if (squareCache[squareId]) {
					displayedSquares.push(squareId);
					displaySquare(squareId);
				} else if (!squaresBeingFetched[squareId]) {
					squaresToFetch.push(squareId);
					squaresBeingFetched[squareId] = squareId;
				}
			});

			if (squaresToFetch.length === 0) return;

			requestStarted();

			fetchSquares(squareSizeMeter.valueOf(), squaresToFetch)
				.then((squares) => {
					console.log('Fetched squares:', squares); // Add this line

					for (const squareId in squaresToFetch) {
						delete squaresBeingFetched[squareId];
					}

					if (!Array.isArray(squares)) {
						throw new Error('Expected squares to be an array');
					}
					squares.forEach((square: any) => {
						while (square.bars.length > maxBarsPerSquare) {
							// Remove random bars until the number of bars is less than or equal to maxBarsPerSquare
							const index = Math.floor(
								Math.random() * square.bars.length,
							);
							square.bars.splice(index, 1);
						}

						squareCache[square.id] = square;

						console.log('square', square);

						if (displayedSquares.includes(square.id)) {
							displaySquare(square.id);
						} else {
							hideSquare(square.id);
						}
					});
				})
				.catch((error) => console.error('Error:', error))
				.finally(() => requestCompleted());
		});
	}

	useEffect(() => {
		initMap();
	}, [squareSizeMeter, maxBarsPerSquare]);

	return (
		<div>
			<div id="gmap" style={{ height: '100vh', width: '100%' }}></div>
			<div id="spinner" className="spinner"></div>
		</div>
	);
};

export default GoPreview;
